<template>
  <p class="mb">
    <strong>{{ tn("keycodes") }}</strong>
  </p>
  <ul :class="enableScrollKeycodes()">
    <li v-for="(n, i) in webrootSubscriptions" :id="`webrootSubscriptionsInfo${i}`" :key="n.licenseKey">
      <div v-for="licenseKey in splitStringByComma(n.licenseKey)" :key="licenseKey" class="licenses">
        <div class="plan-detail">
          {{ getLicenseDescription(n.licenseCategoryName) }}
        </div>
        <div class="keycode-detail">
          <strong class="mr-half">{{ getFormattedKeycode(licenseKey) }}</strong>
          <button :id="`copy${licenseKey}`" class="inline-btn-link" @click="copy(licenseKey)">
            {{ tn("copy") }}
          </button>
        </div>
        <div lass="keycode-expiration">
          <small v-if="!isKeycodeInactive(n.licenseStatusDescription)">
            ({{ tn("expires") }} {{ getFormattedDate(n.expirationDate) }})
          </small>
        </div>
      </div>
    </li>
  </ul>
</template>

<script lang="ts">
import { defineComponent, ref, PropType } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { ISubscriberDownloadsInfo, IWebrootSubscriptions } from "@/common/api/unifiedPortal/interfaces";
import { formatDateString } from "@/common/dateFormat";
import { getWebrootLicenseDescription } from "@/common/webrootProductName";
import { formatKeycode } from "@/common/formatKeycode";
import { isKeycodeExpired } from "@/components/Downloads/isKeycodeExpired";

const componentName = "KeycodesComponent";

export default defineComponent({
  name: componentName,
  props: {
    keycodes: {
      type: Object as PropType<ISubscriberDownloadsInfo>,
      default: null,
    },
  },
  setup(props) {
    logEvent("created", componentName);

    const webrootSubscriptions = ref<IWebrootSubscriptions[]>([]);
    webrootSubscriptions.value =
      props.keycodes?.webrootSubscriptions.filter(
        keycode => !isKeycodeExpired(keycode.expirationDate) || isKeycodeInactive(keycode.licenseStatusDescription)
      ) || [];

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    function getFormattedDate(date: string): string {
      return formatDateString(date);
    }

    function getLicenseDescription(license: string): string | undefined {
      return getWebrootLicenseDescription(license);
    }

    function getFormattedKeycode(keycode: string): string {
      return formatKeycode(keycode);
    }

    function copy(keycodeText) {
      navigator.clipboard.writeText(keycodeText);
    }

    function splitStringByComma(license: string) {
      return license.split(",");
    }

    function isKeycodeInactive(status: string): boolean {
      return status === "Inactive";
    }

    function enableScrollKeycodes(): string {
      if (webrootSubscriptions.value) {
        if (webrootSubscriptions.value.length > 1) {
          return "scroll-Keycodes";
        }

        const keycodes = webrootSubscriptions.value.filter(c => c.licenseKey?.includes(","));
        if (keycodes.length >= 1) {
          return "scroll-Keycodes";
        }
      }
      return "";
    }

    //Expose to template
    return {
      tn,
      webrootSubscriptions,
      enableScrollKeycodes,
      isKeycodeInactive,
      splitStringByComma,
      copy,
      getFormattedKeycode,
      getLicenseDescription,
      getFormattedDate,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
